/**
 * Main JavaScript Entry Point (Refactored)
 * Initializes all components using the component factory pattern
 */

import { FeaturesAccordion } from './components/featuresAccordion.js';
import { StickyNav } from './components/stickyNav.js';
import { MobileNav } from './components/mobileNav.js';
import { initBrandScroller } from './components/brandScroller.js';
import { initTestimonialSlider } from './components/testimonialSlider.js';
import { initHeaderNav } from './components/headerNav.js';
import { initCookieBanner } from './components/consentBanner.js';
import { initCookieSettings } from './components/consentSettings.js';
import { initContactForm } from './components/contactForm.js';
import { initComponent, initComponents } from './utils/componentFactory.js';
import { SELECTORS } from './utils/constants.js';
import { initLazyVideoLoader } from './utils/lazyLoader.js';
import { initAnchorScroll } from './utils/anchorScroll.js';

// Component instances storage
const components = {
  accordions: [],
  stickyNav: null,
  mobileNav: null,
  testimonialSlider: null,
  headerNav: null,
  lazyVideoLoader: null,
  cookieBanner: null,
  cookieSettings: null,
  contactForm: null
};

/**
 * Initialize all accordion components
 */
function initAccordions() {
  const accordionSelectors = [
    SELECTORS.featureAccordion,
    SELECTORS.collectAccordion,
    SELECTORS.analyzeAccordion,
    SELECTORS.shareAccordion
  ];

  accordionSelectors.forEach(selector => {
    const accordion = initComponent(selector, FeaturesAccordion);
    if (accordion) {
      components.accordions.push(accordion);
    }
  });

  console.log(`Initialized ${components.accordions.length} accordion(s)`);
}

/**
 * Initialize sticky navigation
 */
function initNavigation() {
  components.stickyNav = initComponent(SELECTORS.stickyNav, StickyNav);

  if (components.stickyNav) {
    console.log('Sticky navigation initialized');
  }
}

/**
 * Initialize mobile menu toggle
 */
function initMobileMenu() {
  try {
    components.mobileNav = new MobileNav();
    console.log('Mobile navigation initialized');
  } catch (error) {
    console.error('Error initializing mobile navigation:', error);
  }
}

/**
 * Hide the page loader with animation
 */
function hidePageLoader() {
  const loader = document.getElementById('pageLoader');
  if (loader) {
    loader.classList.add('loaded');
    // Remove from DOM after animation completes
    setTimeout(() => {
      loader.remove();
    }, 500);
  }
}

/**
 * Main initialization function
 */
function init() {
  console.log('Brainstork website initializing...');

  try {
    // Initialize all components
    initAccordions();
    initNavigation();
    initMobileMenu();
    initBrandScroller();
    components.testimonialSlider = initTestimonialSlider();
    components.headerNav = initHeaderNav();
    components.lazyVideoLoader = initLazyVideoLoader();
    components.cookieBanner = initCookieBanner();
    components.cookieSettings = initCookieSettings();
    components.contactForm = initContactForm();

    console.log('Brainstork website loaded successfully');

    // Initialize centralized anchor scroll system
    initAnchorScroll();

    // Hide loader after everything is initialized
    hidePageLoader();
  } catch (error) {
    console.error('Error initializing website:', error);
    // Hide loader even if there's an error
    hidePageLoader();
  }
}

/**
 * Clean up function for when page is unloaded
 */
function cleanup() {
  // Destroy all accordion instances
  components.accordions.forEach(accordion => {
    if (accordion && typeof accordion.destroy === 'function') {
      accordion.destroy();
    }
  });

  // Destroy sticky nav
  if (components.stickyNav && typeof components.stickyNav.destroy === 'function') {
    components.stickyNav.destroy();
  }

  // Destroy mobile nav
  if (components.mobileNav && typeof components.mobileNav.destroy === 'function') {
    components.mobileNav.destroy();
  }

  // Destroy testimonial slider
  if (components.testimonialSlider && typeof components.testimonialSlider.destroy === 'function') {
    components.testimonialSlider.destroy();
  }

  // Destroy header nav
  if (components.headerNav && typeof components.headerNav.destroy === 'function') {
    components.headerNav.destroy();
  }

  // Destroy lazy video loader
  if (components.lazyVideoLoader && typeof components.lazyVideoLoader.destroy === 'function') {
    components.lazyVideoLoader.destroy();
  }

  // Destroy cookie banner
  if (components.cookieBanner && typeof components.cookieBanner.destroy === 'function') {
    components.cookieBanner.destroy();
  }

  // Destroy cookie settings
  if (components.cookieSettings && typeof components.cookieSettings.destroy === 'function') {
    components.cookieSettings.destroy();
  }

  // Destroy contact form
  if (components.contactForm && typeof components.contactForm.destroy === 'function') {
    components.contactForm.destroy();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Cleanup on page unload
window.addEventListener('beforeunload', cleanup);

// Export for potential use in other modules
export { components, init, cleanup };
